import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { useTemplates, useSaveSignedContract, useSettings } from '@/shared/hooks/useCloudData';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { useRequirePin } from '@/features/auth/contexts/ClientLockContext';
import { useClientLock } from '@/features/auth/contexts/ClientLockContext';
import { useClients } from '@/features/clients/hooks/useClients';
import { useDraftsForTemplate, useSaveDraft, useDeleteDraft } from '@/shared/hooks/useDrafts';
import { SignaturePad } from '@/features/contracts/components/SignaturePad';
import { Language, SignedContract, DynamicField, TableCellConfig, AddressValue } from '@/shared/types';
import { AddressField } from '@/features/contracts/components/AddressField';
import { PhotoField } from '@/features/contracts/components/PhotoField';
import { generateContractPdf } from '@/features/contracts/services/generatePdf';
import { supabase } from '@/integrations/supabase/client';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronRight, ChevronLeft, Check, AlertCircle, ChevronDown, FileEdit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { RichTextRenderer } from '@/shared/components/RichTextRenderer';
import { substituteVariables, buildSubstitutionVars } from '@/shared/lib/templateVariables';
import { RESERVED_FIELD_KEYS } from '@/shared/constants/fieldKeys';
import { useSubscription } from '@/features/subscription/hooks/useSubscription';
import { useContractQuota } from '@/shared/hooks/useContractQuota';

// ============================================
// Table cell renderer for FillerView
// ============================================

function FillerTableCell({
  cell, language, value, onChange,
}: {
  cell: TableCellConfig;
  language: Language;
  value: string;
  onChange: (val: string) => void;
}) {
  switch (cell.type) {
    case 'static-text':
      return (
        <span className="text-sm text-foreground/80 px-2 py-1">
          {cell.label?.[language] || ''}
        </span>
      );
    case 'checkbox':
      return (
        <label className="flex items-center gap-2 px-2 py-1 cursor-pointer">
          <input
            type="checkbox"
            checked={value === 'true'}
            onChange={e => onChange(e.target.checked ? 'true' : 'false')}
            className="w-5 h-5 rounded accent-primary shrink-0"
          />
          {cell.label?.[language] && (
            <span className="text-sm">{cell.label[language]}</span>
          )}
        </label>
      );
    case 'date':
      return (
        <input
          type="date"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          className="w-full p-2 bg-card border border-border rounded-lg text-sm focus:border-primary focus:outline-none"
        />
      );
    case 'select':
      return (
        <div className="relative">
          <select
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            className="w-full p-2 bg-card border border-border rounded-lg text-sm focus:border-primary focus:outline-none appearance-none pr-7"
          >
            <option value="">—</option>
            {(cell.options?.[language] || []).map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>
      );
    case 'text':
    default:
      return (
        <input
          type="text"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          className="w-full p-2 bg-card border border-border rounded-lg text-sm focus:border-primary focus:outline-none"
        />
      );
  }
}

// ============================================
// Table field renderer
// ============================================

function FillerTableField({
  field, language, fieldValues, onFieldChange,
}: {
  field: DynamicField;
  language: Language;
  fieldValues: Record<string, string>;
  onFieldChange: (key: string, value: string) => void;
}) {
  const config = field.tableConfig;
  if (!config) return null;

  return (
    <div className="space-y-2">
      {field.label[language] && (
        <label className="text-base font-medium flex items-center gap-2">
          {field.label[language]}
          {field.required && <span className="text-destructive text-sm">*</span>}
        </label>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-border rounded-xl">
          <thead>
            <tr>
              {config.columns.map((col, ci) => (
                <th key={ci} className="border border-border bg-muted p-3 text-sm font-semibold text-left">
                  {col[language] || `Col ${ci + 1}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {config.rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => {
                  const cellKey = `${field.key}_r${ri}_c${ci}`;
                  return (
                    <td key={ci} className="border border-border p-2">
                      <FillerTableCell
                        cell={cell}
                        language={language}
                        value={fieldValues[cellKey] || ''}
                        onChange={(val) => onFieldChange(cellKey, val)}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================
// Main Form
// ============================================

const ContractForm = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client_id');
  const sessionId = searchParams.get('sessionId');
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { data: templates = [] } = useTemplates();
  const settings = useSettings();
  const saveContract = useSaveSignedContract();
  const { session } = useAuth();
  const requirePin = useRequirePin();
  const { isLocked } = useClientLock();
  const { clients } = useClients();
  const { tier } = useSubscription();
  const { hasReachedLimit, remaining, isNearLimit, used, limit } = useContractQuota();

  // Find client if coming from client detail
  const linkedClient = clientId ? clients.find(c => c.id === clientId) : null;

  // Draft management
  const saveDraft = useSaveDraft();
  const deleteDraft = useDeleteDraft();
  const { data: existingDrafts = [] } = useDraftsForTemplate(templateId || '', clientId);
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const hasUnsavedChanges = useRef(false);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    templateId === 'new' ? null : templateId ?? null
  );
  const template = templates.find((t) => t.id === selectedTemplateId);

  const [step, setStep] = useState(0);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [checkboxValues, setCheckboxValues] = useState<Record<string, boolean>>({});
  const [checkboxesInitialized, setCheckboxesInitialized] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showParentalForm, setShowParentalForm] = useState(false);
  const [parentalName, setParentalName] = useState('');
  const [parentalRelation, setParentalRelation] = useState('Père');
  const [parentalSig, setParentalSig] = useState('');

  // Show draft modal if existing drafts found (on mount)
  useEffect(() => {
    if (existingDrafts.length > 0 && !currentDraftId) {
      setShowDraftModal(true);
    }
  }, [existingDrafts.length]);

  // Track unsaved changes
  useEffect(() => {
    const hasValues = Object.values(fieldValues).some(v => v && v.trim());
    const hasCheckboxes = Object.values(checkboxValues).some(v => v);
    hasUnsavedChanges.current = hasValues || hasCheckboxes;
  }, [fieldValues, checkboxValues]);

  // Pre-fill field values from linked client
  useEffect(() => {
    if (!linkedClient || !template) return;
    const prefill: Record<string, string> = {};
    const addressObj = (linkedClient.address as Record<string, string>) || {};
    const fullName = linkedClient.name || '';
    const firstName = fullName.split(' ')[0] || '';
    const lastName = fullName.split(' ').slice(1).join(' ') || fullName;

    // Global vars always prefilled
    if (!fieldValues['first_name']) prefill['first_name'] = firstName;
    if (!fieldValues['last_name'])  prefill['last_name']  = lastName;

    template.fields.forEach(field => {
      if (fieldValues[field.key]) return;
      const k = field.key.toLowerCase();
      const fType = field.type;

      // Exact reserved key match — highest priority
      if (field.key === RESERVED_FIELD_KEYS.first_name) {
        prefill[field.key] = firstName;
      } else if (field.key === RESERVED_FIELD_KEYS.last_name) {
        prefill[field.key] = lastName;
      } else if (field.key === RESERVED_FIELD_KEYS.email) {
        prefill[field.key] = linkedClient.email || '';
      } else if (field.key === RESERVED_FIELD_KEYS.phone) {
        prefill[field.key] = linkedClient.phone || '';
      } else if (field.key === RESERVED_FIELD_KEYS.birth_date) {
        prefill[field.key] = linkedClient.birth_date || '';
      } else if (field.key === RESERVED_FIELD_KEYS.address) {
        if (Object.keys(addressObj).length > 0) {
          prefill[field.key] = JSON.stringify({
            street: addressObj.street || '',
            city: addressObj.city || '',
            postalCode: addressObj.postal_code || '',
            country: addressObj.country || 'France',
          });
        }
      // Heuristic fallback for non-reserved keys
      } else if (['first_name','prenom','firstname','prénom','given_name'].some(p => k.includes(p))) {
        prefill[field.key] = firstName;
      } else if (['last_name','lastname','family_name','surname'].some(p => k.includes(p)) && !k.includes('prenom') && !k.includes('first')) {
        prefill[field.key] = lastName;
      } else if (['full_name','nom_complet','client_name','nom_client'].some(p => k === p || k.includes(p))) {
        prefill[field.key] = fullName;
      } else if (fType === 'email' || k.includes('email') || k.includes('mail') || k.includes('courriel')) {
        prefill[field.key] = linkedClient.email || '';
      } else if (fType === 'tel' || ['phone','telephone','portable','mobile'].some(p => k.includes(p))) {
        prefill[field.key] = linkedClient.phone || '';
      } else if (fType === 'date' && ['birth','naissance','birthday','dob','born'].some(p => k.includes(p))) {
        prefill[field.key] = linkedClient.birth_date || '';
      } else if (fType === 'address' || ['address','adresse'].some(p => k.includes(p))) {
        if (Object.keys(addressObj).length > 0) {
          prefill[field.key] = JSON.stringify({
            street: addressObj.street || '',
            city: addressObj.city || '',
            postalCode: addressObj.postal_code || '',
            country: addressObj.country || 'France',
          });
        }
      }
    });

    const nonEmpty = Object.fromEntries(Object.entries(prefill).filter(([, v]) => v));
    if (Object.keys(nonEmpty).length > 0) {
      setFieldValues(prev => ({ ...nonEmpty, ...prev }));
    }
  }, [linkedClient?.id, template?.id]);

  if (template && !checkboxesInitialized) {
    const defaults: Record<string, boolean> = {};
    template.checkboxes.forEach(cb => {
      if (cb.defaultChecked) defaults[cb.id] = true;
    });
    if (Object.keys(defaults).length > 0) {
      setCheckboxValues(prev => ({ ...defaults, ...prev }));
    }
    setCheckboxesInitialized(true);
  }

  if (!template) {
    return (
      <div className="tablet-container pb-28 space-y-6">
        <div className="flex items-center gap-4 pt-6">
          <button
            onClick={() => clientId ? navigate(`/clients/${clientId}`) : navigate('/')}
            className="touch-button p-3 bg-secondary rounded-xl"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="section-title flex-1">{t('nav.newContract')}</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.filter(t => !t.archived).map((tpl) => (
            <button
              key={tpl.id}
              onClick={() => { setSelectedTemplateId(tpl.id); setCheckboxesInitialized(false); }}
              className="card-elevated p-6 text-left flex items-center gap-4 active:scale-[0.98] transition-transform"
            >
              <span className="text-4xl">{tpl.icon}</span>
              <div>
                <p className="text-lg font-bold font-display">{tpl.name[language]}</p>
                <p className="text-sm text-muted-foreground">{tpl.description[language]}</p>
              </div>
              <ChevronRight className="ml-auto text-muted-foreground shrink-0" size={24} />
            </button>
          ))}
        </div>
      </div>
    );
  }

  const updateField = (key: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
  };

  const updateCheckbox = (id: string, checked: boolean) => {
    setCheckboxValues((prev) => ({ ...prev, [id]: checked }));
  };

  const emailRegexValidation = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const getClientAge = (): number | null => {
    if (!template) return null;
    const birthField = template.fields.find(f =>
      f.type === 'date' &&
      ['birth', 'naissance', 'birthday', 'dob', 'born', 'date_nais'].some(p => f.key.toLowerCase().includes(p))
    );
    if (!birthField || !fieldValues[birthField.key]) return null;
    const birth = new Date(fieldValues[birthField.key]);
    if (isNaN(birth.getTime())) return null;
    return (Date.now() - birth.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
  };

  const validate = (): string[] => {
    const errs: string[] = [];
    if (step === 0) {
      template.fields.forEach((f) => {
        if (f.required && f.type !== 'table' && f.type !== 'textarea' && f.type !== 'signature' && f.type !== 'photo' && !fieldValues[f.key]?.trim()) {
          errs.push(f.label[language]);
        }
        if (f.type === 'email' && fieldValues[f.key]?.trim() && !emailRegexValidation.test(fieldValues[f.key].trim())) {
          errs.push(`${f.label[language]} (${language === 'fr' ? 'format invalide' : language === 'es' ? 'formato inválido' : 'invalid format'})`);
        }
        if (f.required && f.type === 'signature' && !fieldValues[f.key]) {
          errs.push(f.label[language]);
        }
        if (f.required && f.type === 'photo' && !fieldValues[f.key]) {
          errs.push(f.label[language]);
        }
      });

      const age = getClientAge();
      if (age !== null) {
        if (age < 5 || age > 120) {
          errs.push(language === 'fr' ? 'Date de naissance invalide' : 'Invalid date of birth');
        } else if (age < 18) {
          const cb18 = template.checkboxes.find(c =>
            Object.values(c.label).join(' ').toLowerCase().includes('18')
          );
          if (cb18 && checkboxValues[cb18.id]) {
            errs.push(language === 'fr'
              ? 'La date de naissance indique que vous avez moins de 18 ans'
              : 'Date of birth indicates the client is under 18');
          }
        }
      }
    }
    if (step === 1) {
      template.checkboxes.forEach((c) => {
        if (c.required && !checkboxValues[c.id]) {
          errs.push(c.label[language]);
        }
      });
    }
    return errs;
  };

  const totalSteps = 2;

  const handleSaveDraft = async () => {
    if (!template) return;
    const clientName = fieldValues['first_name'] && fieldValues['last_name']
      ? `${fieldValues['first_name']} ${fieldValues['last_name']}`
      : fieldValues['NOM_CLIENT'] || linkedClient?.name || '';

    const doSaveAndLeave = async () => {
      const draftId = await saveDraft.mutateAsync({
        id: currentDraftId || undefined,
        template_id: template.id,
        template_name: template.name[language],
        client_id: clientId || null,
        client_name: clientName,
        field_values: fieldValues,
        checkbox_values: checkboxValues,
        step,
        language,
      });
      setCurrentDraftId(draftId);
      toast.success(language === 'fr' ? 'Brouillon sauvegardé' : 'Draft saved');
      hasUnsavedChanges.current = false;
      if (pendingNavigation) {
        const dest = pendingNavigation;
        setPendingNavigation(null);
        navigate(dest);
      }
      setShowExitModal(false);
    };

    if (isLocked) {
      setShowExitModal(false);
      requirePin(() => { doSaveAndLeave(); }, { releaseLock: true });
    } else {
      doSaveAndLeave();
    }
  };

  const handleLoadDraft = (draft: (typeof existingDrafts)[0]) => {
    setFieldValues(draft.field_values);
    setCheckboxValues(draft.checkbox_values);
    setStep(draft.step);
    setCurrentDraftId(draft.id);
    setShowDraftModal(false);
  };

  const handleDiscardDraft = async (draftId: string) => {
    await deleteDraft.mutateAsync(draftId);
    setShowDraftModal(false);
  };

  const handleDiscardAndLeave = () => {
    const dest = pendingNavigation;
    setShowExitModal(false);
    setPendingNavigation(null);
    if (!dest) return;
    if (isLocked) {
      requirePin(() => {
        hasUnsavedChanges.current = false;
        navigate(dest);
      }, { releaseLock: true });
    } else {
      hasUnsavedChanges.current = false;
      navigate(dest);
    }
  };

  const handleBackWithCheck = (destination: string) => {
    if (hasUnsavedChanges.current) {
      setPendingNavigation(destination);
      setShowExitModal(true);
    } else {
      navigate(destination);
    }
  };

  const handleNext = () => {
    const errs = validate();
    if (errs.length > 0) { setErrors(errs); return; }
    setErrors([]);
    if (step < totalSteps - 1) setStep(step + 1);
  };

  const handleBack = () => {
    setErrors([]);
    if (step > 0) setStep(step - 1);
  };

  const inferClientName = () => {
    const composedName = [fieldValues.first_name, fieldValues.last_name]
      .map((s) => (s || '').trim())
      .filter(Boolean)
      .join(' ');
    const textField = template.fields.find((f) => f.type === 'text' && f.required);
    return (
      composedName ||
      fieldValues['NOM_CLIENT'] ||
      fieldValues['CLIENT_NAME'] ||
      (textField ? fieldValues[textField.key] : '') ||
      ''
    );
  };

  const inferClientEmail = () => {
    const emailField = template.fields.find((f) => f.type === 'email');
    return (
      fieldValues['EMAIL'] ||
      fieldValues['CLIENT_EMAIL'] ||
      (emailField ? fieldValues[emailField.key] : '') ||
      ''
    );
  };

  const blobToBase64 = async (blob: Blob) => {
    const buffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = '';
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (errs.length > 0) { setErrors(errs); return; }

    const signatureField = template.fields.find(f => f.type === 'signature');
    const signatureDataUrl = signatureField ? fieldValues[signatureField.key] || '' : '';
    const clientName = inferClientName();
    const clientEmail = inferClientEmail();

    const contract: SignedContract = {
      id: crypto.randomUUID(),
      templateId: template.id,
      templateName: template.name[language],
      clientId: clientId || null,
      clientName,
      clientEmail,
      language,
      fieldValues,
      checkboxValues,
      signatureDataUrl,
      signedAt: new Date().toISOString(),
      practitionerEmail: settings.practitionerEmail,
    };

    try {
      setIsSendingEmail(true);
      await saveContract.mutateAsync(contract);

      // ── Sync contract fields back to client profile if fields were empty ──
      if (clientId && linkedClient) {
        const clientUpdates: Record<string, any> = {};

        // Find email in fieldValues
        if (!linkedClient.email) {
          const emailVal = Object.entries(fieldValues).find(([k, v]) => {
            const field = template.fields.find(f => f.key === k);
            return (field?.type === 'email' || k.toLowerCase().includes('email') || k.toLowerCase().includes('mail')) && v && v.includes('@');
          })?.[1];
          if (emailVal) clientUpdates.email = emailVal.trim();
        }

        // Find phone in fieldValues
        if (!linkedClient.phone) {
          const phoneVal = Object.entries(fieldValues).find(([k, v]) => {
            const field = template.fields.find(f => f.key === k);
            return (field?.type === 'tel' || ['phone','telephone','tel','portable','mobile'].some(p => k.toLowerCase().includes(p))) && v;
          })?.[1];
          if (phoneVal) clientUpdates.phone = phoneVal.trim();
        }

        // Find address in fieldValues
        if (!linkedClient.address || Object.keys(linkedClient.address).length === 0) {
          const addrVal = Object.entries(fieldValues).find(([k, v]) => {
            const field = template.fields.find(f => f.key === k);
            return (field?.type === 'address' || k.toLowerCase().includes('adress') || k.toLowerCase().includes('address')) && v;
          })?.[1];
          if (addrVal) {
            try { const parsed = JSON.parse(addrVal); if (typeof parsed === 'object') clientUpdates.address = parsed; } catch { /* not json */ }
          }
        }

        if (Object.keys(clientUpdates).length > 0) {
          await (supabase as any).from('clients').update(clientUpdates).eq('id', clientId).eq('owner_id', session?.user?.id);
        }
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const hasValidClientEmail = !!clientEmail && emailRegex.test(clientEmail.trim());
      const hasValidPractitionerEmail = !!settings.practitionerEmail && emailRegex.test(settings.practitionerEmail.trim());

      if (tier !== 'free' && (hasValidClientEmail || hasValidPractitionerEmail)) {
        const pdfBlob = generateContractPdf(contract, template, 'blob', {
          logoDataUrl: settings.logoUrl,
          businessName: settings.businessName || settings.practitionerName,
        }) as Blob;
        if (pdfBlob) {
          const pdfBase64 = await blobToBase64(pdfBlob);
          const { data: { session } } = await supabase.auth.getSession();
          const { data: emailData, error: emailError } = await supabase.functions.invoke('send-contract-email', {
            headers: {
              Authorization: `Bearer ${session?.access_token}`,
            },
            body: {
              clientEmail: hasValidClientEmail ? clientEmail.trim() : null,
              practitionerEmail: hasValidPractitionerEmail ? settings.practitionerEmail.trim() : null,
              clientName,
              practitionerName: settings.practitionerName,
              templateName: contract.templateName,
              signedAt: contract.signedAt,
              pdfBase64,
            },
          });
          if (emailError) console.error('Email error:', emailError);

          if (emailError) {
            toast.warning(language === 'fr' ? 'Contrat sauvegardé, mais email non envoyé.' : 'Contract saved, but email not sent.');
          } else if (emailData && !emailData.allSent) {
            toast.warning(language === 'fr'
              ? 'Contrat sauvegardé. Certains emails n\'ont pas pu être envoyés (domaine Resend non vérifié).'
              : 'Contract saved. Some emails could not be sent (Resend domain not verified).');
          }
        }
      }

      const ageAtSubmit = getClientAge();
      const cb18AtSubmit = template.checkboxes.find(c =>
        Object.values(c.label).join(' ').toLowerCase().includes('18')
      );
      if (ageAtSubmit !== null && ageAtSubmit < 18 && !(cb18AtSubmit && checkboxValues[cb18AtSubmit.id])) {
        setShowParentalForm(true);
        setIsSendingEmail(false);
        return;
      }

      toast.success(t('form.success'));
      // Delete draft if one existed
      if (currentDraftId) {
        await deleteDraft.mutateAsync(currentDraftId);
        setCurrentDraftId(null);
      }
      if (sessionId) {
        navigate(`/sessions/new?sessionId=${sessionId}&contractId=${contract.id}`);
      } else if (clientId) {
        navigate(`/clients/${clientId}`);
      } else {
        navigate('/history');
      }
    } catch {
      toast.error(language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Error saving contract');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const substitutionVars = buildSubstitutionVars({
    fieldValues,
    practitionerName: settings.practitionerName,
    language,
  });
  const renderContent = (content: string) => substituteVariables(content, substitutionVars);

  return (
    <div className="tablet-container pb-28 space-y-6">
      <div className="flex items-center gap-4 pt-6">
        <button
          onClick={() => (step > 0 ? handleBack() : handleBackWithCheck(clientId ? `/clients/${clientId}` : '/templates'))}
          className="touch-button p-3 bg-secondary rounded-xl"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold font-display">{template.name[language]}</h1>
          <p className="text-sm text-muted-foreground">
            {t('form.step')} {step + 1} {t('common.of')} {totalSteps}
          </p>
        </div>
      </div>

      <div className="flex gap-2">
        {[0, 1, 2].map((s) => (
          <div key={s} className={`h-2 flex-1 rounded-full transition-colors ${s <= step ? 'bg-primary' : 'bg-border'}`} />
        ))}
      </div>

      {/* ── Quota banner (Starter only) ───────────────────────────────── */}
      {tier === 'free' && (
        hasReachedLimit ? (
          <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="text-destructive shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-semibold text-destructive text-sm">
                {language === 'fr' ? 'Limite mensuelle atteinte' : language === 'es' ? 'Límite mensual alcanzado' : 'Monthly limit reached'}
              </p>
              <p className="text-xs text-destructive/80 mt-0.5">
                {language === 'fr'
                  ? `Vous avez utilisé ${used} / ${limit} contrats ce mois-ci. Passez au plan Solo pour des contrats illimités.`
                  : language === 'es'
                    ? `Has usado ${used} / ${limit} contratos este mes. Pasa al plan Solo para contratos ilimitados.`
                    : `You've used ${used} / ${limit} contracts this month. Upgrade to Solo for unlimited contracts.`}
              </p>
              <button
                onClick={() => navigate('/pricing')}
                className="mt-2 text-xs font-semibold text-destructive underline underline-offset-2"
              >
                {language === 'fr' ? 'Voir les plans →' : language === 'es' ? 'Ver planes →' : 'View plans →'}
              </button>
            </div>
          </div>
        ) : isNearLimit ? (
          <div className="bg-warning/10 border border-warning/30 rounded-xl p-3 flex items-center gap-3">
            <AlertCircle className="text-warning shrink-0" size={16} />
            <p className="text-xs text-warning font-medium">
              {language === 'fr'
                ? `Plus que ${remaining} contrat${remaining > 1 ? 's' : ''} disponible${remaining > 1 ? 's' : ''} ce mois-ci (plan Starter).`
                : language === 'es'
                  ? `Solo quedan ${remaining} contrato${remaining > 1 ? 's' : ''} este mes (plan Starter).`
                  : `Only ${remaining} contract${remaining > 1 ? 's' : ''} left this month (Starter plan).`}
            </p>
          </div>
        ) : null
      )}

      <AnimatePresence>
        {errors.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 flex items-start gap-3"
          >
            <AlertCircle className="text-destructive shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-semibold text-destructive">{t('form.fillRequired')}</p>
              <ul className="text-sm text-destructive/80 mt-1 list-disc list-inside">
                {errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          {step === 0 && (
            <div className="space-y-4">
              <h2 className="section-title">{t('form.clientInfo')}</h2>

              {template.fields.map((field) => {
                // Table field
                if (field.type === 'table') {
                  return (
                    <FillerTableField
                      key={field.id}
                      field={field}
                      language={language}
                      fieldValues={fieldValues}
                      onFieldChange={updateField}
                    />
                  );
                }

                // Address field
                if (field.type === 'address') {
                  const addrRaw = fieldValues[field.key];
                  let addrValue: AddressValue;
                  try {
                    addrValue = addrRaw ? JSON.parse(addrRaw) : {
                      country: field.addressConfig?.defaultCountry || 'France',
                      city: '', postalCode: '', street: '', department: '',
                    };
                  } catch {
                    addrValue = {
                      country: field.addressConfig?.defaultCountry || 'France',
                      city: '', postalCode: '', street: '', department: '',
                    };
                  }
                  return (
                    <AddressField
                      key={field.id}
                      value={addrValue}
                      onChange={(val) => updateField(field.key, JSON.stringify(val))}
                      language={language}
                      required={field.required}
                      showDepartment={field.addressConfig?.showDepartment}
                      defaultCountry={field.addressConfig?.defaultCountry}
                      label={field.label[language]}
                    />
                  );
                }

                // Textarea → static read-only text block
                if (field.type === 'textarea') {
                  const content = field.placeholder?.[language] || '';
                  if (!content) return null;
                  return (
                    <div key={field.id} className="space-y-2">
                      {field.label[language] && (
                        <label className="text-base font-semibold">{field.label[language]}</label>
                      )}
                      <div className="p-4 bg-muted/50 border border-border rounded-xl text-base leading-relaxed text-foreground/90">
                        <RichTextRenderer html={renderContent(content)} />
                      </div>
                    </div>
                  );
                }

                // Signature → signature pad
                if (field.type === 'signature') {
                  return (
                    <div key={field.id} className="space-y-2">
                      <label className="text-base font-medium flex items-center gap-2">
                        {field.label[language]}
                        {field.required && <span className="text-destructive text-sm">*</span>}
                      </label>
                      <SignaturePad onSignatureChange={(dataUrl) => updateField(field.key, dataUrl || '')} />
                    </div>
                  );
                }

                // Photo → camera/upload
                if (field.type === 'photo') {
                  return (
                    <PhotoField
                      key={field.id}
                      value={fieldValues[field.key] || null}
                      onChange={(dataUrl) => updateField(field.key, dataUrl || '')}
                      language={language}
                      label={field.label[language]}
                      required={field.required}
                    />
                  );
                }

                return (
                  <div key={field.id} className="space-y-2">
                    <label className="text-base font-medium flex items-center gap-2">
                      {field.label[language]}
                      {field.required && <span className="text-destructive text-sm">*</span>}
                    </label>
                    {field.type === 'select' ? (
                      <div className="relative">
                        <select
                          value={fieldValues[field.key] || ''}
                          onChange={(e) => updateField(field.key, e.target.value)}
                          className="w-full p-4 bg-card border-2 border-border rounded-xl text-lg focus:border-primary focus:outline-none transition-colors appearance-none pr-10"
                        >
                          <option value="">{field.placeholder?.[language] || '—'}</option>
                          {(field.options?.[language] || []).map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                          ))}
                        </select>
                        <ChevronDown size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                      </div>
                    ) : (
                      <input
                        type={field.type}
                        value={fieldValues[field.key] || ''}
                        onChange={(e) => updateField(field.key, e.target.value)}
                        placeholder={field.placeholder?.[language]}
                        pattern={field.type === 'email' ? '[^\\s@]+@[^\\s@]+\\.[^\\s@]+' : undefined}
                        className={`w-full p-4 bg-card border-2 rounded-xl text-lg focus:border-primary focus:outline-none transition-colors ${
                          field.type === 'email' && fieldValues[field.key]?.trim() && !emailRegexValidation.test(fieldValues[field.key].trim())
                            ? 'border-destructive'
                            : 'border-border'
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              {template.sections.map((section) => (
                <div key={section.id} className="card-elevated p-6 space-y-3">
                  <h3 className="text-lg font-bold font-display">{section.title[language]}</h3>
                  <RichTextRenderer
                    html={renderContent(section.content[language])}
                    className="text-base leading-relaxed text-foreground/90"
                  />
                </div>
              ))}

              <h2 className="section-title">{t('form.consent')}</h2>
              <div className="space-y-3">
                {template.checkboxes.map((cb) => (
                  <label
                    key={cb.id}
                    className={`card-elevated p-5 flex items-center gap-4 cursor-pointer ${
                      checkboxValues[cb.id] ? 'ring-2 ring-primary bg-primary/5' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={!!checkboxValues[cb.id]}
                      onChange={(e) => updateCheckbox(cb.id, e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-7 h-7 shrink-0 rounded-lg border-2 flex items-center justify-center transition-colors ${
                      checkboxValues[cb.id] ? 'bg-primary border-primary' : 'border-border'
                    }`}>
                      {checkboxValues[cb.id] && <Check size={18} className="text-primary-foreground" />}
                    </div>
                    <span className="text-base leading-relaxed flex-1">
                      <RichTextRenderer html={renderContent(cb.label[language])} />
                      {cb.required && <span className="text-destructive ml-1">*</span>}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

        </motion.div>
      </AnimatePresence>

      <div className="flex gap-4 pt-4">
        {step > 0 && (
          <button onClick={handleBack} className="touch-button flex-1 flex items-center justify-center gap-2 bg-secondary text-secondary-foreground rounded-xl py-4">
            <ChevronLeft size={20} />
            {t('common.previous')}
          </button>
        )}
        {step < totalSteps - 1 ? (
          <button onClick={handleNext} className="touch-button flex-1 flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl py-4 font-semibold">
            {t('common.next')}
            <ChevronRight size={20} />
          </button>
        ) : (
          <button
            onClick={() => requirePin(handleSubmit, { releaseLock: true })}
            disabled={saveContract.isPending || isSendingEmail || hasReachedLimit}
            className="touch-button-lg flex-1 flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-2xl py-4 font-bold text-xl disabled:opacity-50"
          >
            {saveContract.isPending || isSendingEmail ? (
              <div className="w-6 h-6 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
            ) : (
              <>
                <Check size={24} />
                {t('form.signAndSend')}
              </>
            )}
          </button>
        )}
      </div>

      {/* ── Accord parental (mineur) ──────────────────────────────────── */}
      <AnimatePresence>
        {showParentalForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }}
              className="glass-card p-6 max-w-md w-full space-y-5 shadow-xl border border-border/50">
              <h2 className="text-xl font-bold font-display">
                {language === 'fr' ? 'Accord parental requis' : 'Parental consent required'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {language === 'fr'
                  ? "Le client est mineur. La signature d'un représentant légal est requise."
                  : 'The client is a minor. A legal guardian\'s signature is required.'}
              </p>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    {language === 'fr' ? 'Nom du représentant légal *' : 'Legal guardian name *'}
                  </label>
                  <input
                    value={parentalName}
                    onChange={e => setParentalName(e.target.value)}
                    placeholder="Prénom Nom"
                    className="w-full p-3 bg-background border-2 border-border rounded-xl text-sm focus:border-primary focus:outline-none"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    {language === 'fr' ? 'Lien de parenté' : 'Relationship'}
                  </label>
                  <select
                    value={parentalRelation}
                    onChange={e => setParentalRelation(e.target.value)}
                    className="w-full p-3 bg-background border-2 border-border rounded-xl text-sm focus:border-primary focus:outline-none"
                  >
                    <option value="Père">Père</option>
                    <option value="Mère">Mère</option>
                    <option value="Tuteur légal">Tuteur légal</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    {language === 'fr' ? 'Signature du représentant légal *' : 'Guardian signature *'}
                  </label>
                  <SignaturePad onSignatureChange={v => setParentalSig(v ?? '')} />
                </div>
              </div>
              <button
                disabled={!parentalName.trim() || !parentalSig}
                onClick={() => {
                  if (!parentalName.trim() || !parentalSig) return;
                  setShowParentalForm(false);
                  toast.success(t('form.success'));
                  if (clientId) navigate(`/clients/${clientId}`);
                  else navigate('/history');
                }}
                className="touch-button-lg w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-2xl py-4 font-bold disabled:opacity-50"
              >
                <Check size={22} />
                {language === 'fr' ? 'Confirmer et signer' : 'Confirm and sign'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Draft picker modal ────────────────────────────────────────── */}
      <AnimatePresence>
        {showDraftModal && existingDrafts.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            onClick={() => setShowDraftModal(false)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              onClick={e => e.stopPropagation()}
              className="bg-card border border-border rounded-2xl p-6 max-w-sm w-full space-y-4 shadow-xl">
              <div className="flex items-center gap-2">
                <FileEdit size={20} className="text-primary" />
                <h3 className="font-bold text-lg">
                  {language === 'fr' ? 'Brouillons existants' : 'Existing drafts'}
                </h3>
              </div>
              <p className="text-sm text-muted-foreground">
                {language === 'fr'
                  ? 'Vous avez des brouillons pour ce contrat. Voulez-vous reprendre l\'un d\'eux ?'
                  : 'You have drafts for this contract. Would you like to resume one?'}
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {existingDrafts.map(draft => (
                  <div key={draft.id} className="flex items-center justify-between p-3 rounded-xl bg-secondary/50 border border-border/50">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{draft.client_name || draft.template_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(draft.updated_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <div className="flex gap-1.5 shrink-0 ml-2">
                      <button onClick={() => handleLoadDraft(draft)}
                        className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium rounded-lg hover:bg-primary/90">
                        {language === 'fr' ? 'Reprendre' : 'Resume'}
                      </button>
                      <button onClick={() => handleDiscardDraft(draft.id)}
                        className="p-1.5 text-destructive hover:bg-destructive/10 rounded-lg">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => setShowDraftModal(false)}
                className="w-full py-2.5 border border-border/50 rounded-xl text-sm text-muted-foreground hover:bg-secondary transition-colors">
                {language === 'fr' ? 'Recommencer à zéro' : 'Start fresh'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Exit confirmation modal ────────────────────────────────────── */}
      <AnimatePresence>
        {showExitModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            onClick={() => setShowExitModal(false)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              onClick={e => e.stopPropagation()}
              className="bg-card border border-border rounded-2xl p-6 max-w-sm w-full space-y-4 shadow-xl">
              <h3 className="font-bold text-lg">
                {language === 'fr' ? 'Quitter le formulaire ?' : 'Leave the form?'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {language === 'fr'
                  ? 'Des informations ont été saisies. Souhaitez-vous sauvegarder un brouillon ?'
                  : 'Some information has been entered. Would you like to save a draft?'}
              </p>
              <div className="flex flex-col gap-2">
                <button onClick={handleSaveDraft} disabled={saveDraft.isPending}
                  className="w-full py-3 bg-primary text-primary-foreground font-semibold rounded-xl hover:bg-primary/90 disabled:opacity-50">
                  {language === 'fr' ? '💾 Sauvegarder le brouillon' : '💾 Save draft'}
                </button>
                <button onClick={handleDiscardAndLeave}
                  className="w-full py-3 border border-destructive/30 text-destructive font-medium rounded-xl hover:bg-destructive/10">
                  {language === 'fr' ? 'Abandonner les modifications' : 'Discard changes'}
                </button>
                <button onClick={() => setShowExitModal(false)}
                  className="w-full py-3 border border-border/50 text-muted-foreground rounded-xl hover:bg-secondary">
                  {language === 'fr' ? 'Continuer le formulaire' : 'Continue filling'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContractForm;
